import { Navigate, useLocation } from 'react-router-dom'
import { useRBACStore } from '@/stores/rbac-store'
import { useAuthStore } from '@/stores/auth-store'
import { Loader2, AlertCircle } from 'lucide-react'
import { useAuth } from '../contexts/useAuth'
import { toast } from 'sonner'
import { hasRole, hasMinRoleLevel, hasEmailAccess, UserRole, getRouteConfig } from '@/config/rbac.config'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface RequireRoleProps {
  children: React.ReactNode
  allowedRoles?: UserRole[]
  minRoleLevel?: number
  fallbackPath?: string
  showUnauthorized?: boolean
  routePath?: string // Auto-detect from route config if provided
}

export function RequireRole({ 
  children, 
  allowedRoles,
  minRoleLevel,
  fallbackPath = '/dashboard',
  showUnauthorized = false,
  routePath
}: RequireRoleProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { userRole, isLoading: rbacLoading } = useRBACStore()
  const { user } = useAuthStore()
  const location = useLocation()

  // Auto-detect configuration from route if routePath is provided
  const routeConfig = routePath ? getRouteConfig(routePath) : null;
  const effectiveAllowedRoles = allowedRoles || routeConfig?.allowedRoles || [];
  const effectiveAllowedEmails = routeConfig?.allowedEmails || [];
  const effectiveFallbackPath = fallbackPath || routeConfig?.fallback || '/dashboard';

  // Show loading while auth or RBAC is loading
  if (authLoading || rbacLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role permissions
  const hasRoleAccess = effectiveAllowedRoles.length > 0 
    ? hasRole(userRole, effectiveAllowedRoles)
    : true;
    
  const hasLevelAccess = minRoleLevel !== undefined 
    ? hasMinRoleLevel(userRole, minRoleLevel)
    : true;

  // Check email-based access
  const hasEmailBasedAccess = effectiveAllowedEmails.length > 0
    ? hasEmailAccess(user?.attributes?.email, effectiveAllowedEmails)
    : false;

  const isAuthorized = (hasRoleAccess && hasLevelAccess) || hasEmailBasedAccess;

  if (!isAuthorized) {
    // Log unauthorized access attempt
    console.warn(`Unauthorized access attempt to ${location.pathname} by role: ${userRole}`, {
      userRole,
      userEmail: user?.attributes?.email,
      requiredRoles: effectiveAllowedRoles,
      allowedEmails: effectiveAllowedEmails,
      minRoleLevel,
      path: location.pathname
    });

    // Show toast notification
    toast.error('You do not have permission to access this page');

    // Show unauthorized page or redirect
    if (showUnauthorized) {
      return (
        <div className="flex h-screen w-screen items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle>Access Denied</CardTitle>
              <CardDescription>
                You don't have permission to access this page.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="mb-4 text-sm text-muted-foreground">
                {userRole ? `Your current role: ${userRole}` : 'No role assigned'}
              </p>
              <Button onClick={() => window.history.back()} variant="outline" className="mr-2">
                Go Back
              </Button>
              <Button onClick={() => window.location.href = effectiveFallbackPath}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return <Navigate to={effectiveFallbackPath} replace />;
  }

  return <>{children}</>
}